import { Analytics } from '@cobliteam/analytics';
import { DeviceGeneralStatus } from '@cobliteam/api-lib/src/__generated__/graphql';
import { type Vehicle, getDeviceStatusLabel } from '@cobliteam/common-types';
import { cn } from '@cobliteam/ui/utils';
import diacritics from 'diacritics';
import { fleetCategories } from './sidebarDef';
import { type ColorKeys, type EnumCategory, EnumDeviceType } from './types';

const trackGlobalSearchEvents = {
  openSearch: () => {
    Analytics.track('Global Search - Open', {
      source: 'global_search',
    });
  },

  inputFocus: () => {
    Analytics.track('Global Search - Input Focus', {
      source: 'global_search',
    });
  },

  search: (query: string) => {
    Analytics.track('Global Search - Query', {
      source: 'global_search',
      query,
    });
  },

  categoryChange: (category: EnumCategory) => {
    Analytics.track('Global Search - Category Change', {
      source: 'global_search',
      category,
    });
  },

  itemClick: (itemId: string, itemType: EnumCategory, itemName: string) => {
    Analytics.track('Global Search - Item Click', {
      source: 'global_search',
      itemId,
      itemType,
      itemName,
    });
  },

  updateData: () => {
    Analytics.track('Global Search - Update All Data', {
      source: 'global_search',
    });
  },
};

const parseContent = (content: string) => {
  return content.split('\n').map((line, index) => (
    <p
      key={index}
      className={cn('mb-2 text-xs', {
        'font-medium text-xs': line.match(/^\d+\./),
      })}
    >
      {line}
    </p>
  ));
};

const removeDiacritics = (text: string) => {
  return diacritics
    .remove(text)
    .replace(/[^a-zA-Z0-9\s]/g, '')
    .trim();
};

const deviceStatusLabel = (status: DeviceGeneralStatus) => {
  return status === DeviceGeneralStatus.LosingData ? 'Perdendo dados' : getDeviceStatusLabel(status);
};

const deviceTypeLabel = (type?: string) => {
  if (!type) return '---';
  return EnumDeviceType[type as keyof typeof EnumDeviceType] || type;
};

const vehicleLabel = (vehicle: Vehicle) => {
  return vehicle
    ? `${vehicle.model.brand || vehicle.model.name || vehicle.model.year ? '' : '---'}
      ${vehicle.model.brand ? `${vehicle.model.brand} - ` : ''}
      ${vehicle.model.name ? `${vehicle.model.name} - ` : ''}
      ${vehicle.model.year && vehicle.model.year !== '0' ? `(${vehicle.model.year})` : ''}
      `
    : '---';
};

const activeBarColor = (color?: ColorKeys) => {
  if (!color) return 'after:bg-gray-12';
  const variants: Record<ColorKeys, string> = {
    gray: 'after:bg-gray-12',
    primary: 'after:bg-primary',
    warning: 'after:bg-warning',
    success: 'after:bg-success',
    'accessory-purple': 'after:bg-accessory-purple',
    'accessory-sky': 'after:bg-accessory-sky',
  };
  return variants[color as keyof typeof variants] || 'after:bg-gray-12';
};

const cardColorClass = (color?: ColorKeys) => {
  if (!color) return 'bg-gray-2 border-gray-3 hover:border-gray-4';
  const variants: Record<ColorKeys, string> = {
    primary: 'bg-primary-2 border-primary-3 hover:border-primary-4',
    warning: 'bg-warning-2 border-warning-3 hover:border-warning-4',
    success: 'bg-success-2 border-success-3 hover:border-success-4',
    gray: 'bg-gray-2 border-gray-3 hover:border-gray-4',
    'accessory-sky': 'bg-accessory-sky-2 border-accessory-sky-3 hover:border-accessory-sky-4',
    'accessory-purple': 'bg-accessory-purple-2 border-accessory-purple-3 hover:border-accessory-purple-4',
  };
  return variants[color as keyof typeof variants] || 'bg-gray-2 border-gray-3 hover:border-gray-4';
};

const cardIconColorClass = (color?: string) => {
  if (!color) return 'border-gray-6';
  const variants: Record<ColorKeys, string> = {
    primary: 'border-primary-6',
    warning: 'border-warning-4',
    success: 'border-success-6',
    gray: 'border-gray-6',
    'accessory-sky': 'border-accessory-sky-6',
    'accessory-purple': 'border-accessory-purple-6',
  };
  return variants[color as keyof typeof variants] || 'border-gray-6';
};

const iconColorClass = (color?: string) => {
  if (!color) return '!text-gray';
  const variants: Record<ColorKeys, string> = {
    primary: '!text-primary',
    warning: '!text-warning',
    success: '!text-success',
    gray: '!text-gray',
    'accessory-sky': '!text-accessory-sky-11',
    'accessory-purple': '!text-accessory-purple-11',
  };
  return variants[color as keyof typeof variants] || '!text-gray';
};

const buttonColorLink = (color?: string) => {
  if (!color) return 'primary';
  const variants: Record<ColorKeys, string> = {
    primary: 'primary',
    warning: 'warning',
    success: 'success',
    gray: 'gray',
    'accessory-sky': 'success',
    'accessory-purple': 'accessory-purple',
  };
  return variants[color as keyof typeof variants] || 'primary';
};

const getCategory = (id: EnumCategory) => {
  return fleetCategories.find((category) => category.id === id);
};

export {
  trackGlobalSearchEvents,
  parseContent,
  removeDiacritics,
  deviceTypeLabel,
  deviceStatusLabel,
  activeBarColor,
  vehicleLabel,
  cardColorClass,
  cardIconColorClass,
  iconColorClass,
  buttonColorLink,
  getCategory,
};
